import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, useAuth } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import {
  Package, Thermometer, AlertTriangle, ArrowRight, Loader2,
  Wind, ClipboardList, CheckCircle2, Clock, Sparkles,
  Activity as ActivityIcon, Tag, Circle,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, formatDistanceToNow, isToday } from "date-fns";
import { es as esLocale, fr as frLocale } from "date-fns/locale";

// ─────────────────────────────────────────────
// Dashboard — Operations Center
// ─────────────────────────────────────────────
// Answers, in ≤10 s of reading:
//   1) Is everything OK?
//   2) What needs my attention?
//   3) What just happened?
//   4) Is there a food-safety risk?
// Five blocks, in that visual priority.
// ─────────────────────────────────────────────

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const dateLocale = lang === "fr" ? frLocale : esLocale;
  const firstName = user?.name?.split(" ")?.[0] || (lang === "fr" ? "équipe" : "equipo");
  const todayLabel = format(
    new Date(),
    lang === "fr" ? "EEEE d MMMM" : "EEEE d 'de' MMMM",
    { locale: dateLocale }
  );

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);
  const [data, setData] = useState({
    stats: null,
    expiringProducts: [],
    closingItems: [],
    closingCompletedToday: false,
    pendingPurchases: 0,
    cooling: [],
    labelHistory: [],
    tempLogs: [],
    refrigerators: [],
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setLoadError(false);
      try {
        const [
          statsRes, expiringRes, closingItemsRes, closingStatusRes,
          purchaseRes, coolingRes, labelHistRes, tempLogsRes, fridgesRes,
        ] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/products?status=warning"),
          api.get("/closing-checklist/items"),
          api.get("/closing-checklist/status"),
          api.get("/purchase-list"),
          api.get("/cooling"),
          api.get("/labels/history"),
          api.get("/temperature-logs"),
          api.get("/refrigerators"),
        ]);
        if (cancelled) return;
        setData({
          stats: statsRes.data,
          expiringProducts: expiringRes.data || [],
          closingItems: closingItemsRes.data || [],
          closingCompletedToday: Boolean(closingStatusRes.data?.completed_today),
          pendingPurchases: (purchaseRes.data || []).length,
          cooling: coolingRes.data || [],
          labelHistory: labelHistRes.data || [],
          tempLogs: tempLogsRes.data || [],
          refrigerators: fridgesRes.data || [],
        });
        setLoadedAt(new Date());
      } catch (err) {
        if (cancelled) return;
        console.error("[Dashboard] load failed:", err);
        setLoadError(true);
        toast.error("Error al cargar el panel");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // ── Derived facts ────────────────────────
  const facts = useMemo(() => {
    const { refrigerators, tempLogs, cooling, closingItems, closingCompletedToday, expiringProducts, stats } = data;

    // Latest temp log per refrigerator + fridges without-any-log-today
    const latestByFridge = new Map();
    for (const l of tempLogs) {
      const prev = latestByFridge.get(l.refrigerator_id);
      if (!prev || new Date(l.logged_at) > new Date(prev.logged_at)) {
        latestByFridge.set(l.refrigerator_id, l);
      }
    }
    let fridgesOK = 0;
    let fridgesOOR = 0;
    const fridgesWithoutTodayLog = [];
    const oorFridges = [];
    for (const f of refrigerators) {
      const latest = latestByFridge.get(f.refrigerator_id);
      const hasTodayLog = latest && (() => {
        try { return isToday(parseISO(latest.logged_at)); } catch { return false; }
      })();
      if (!hasTodayLog) fridgesWithoutTodayLog.push(f);
      if (!latest) continue;
      if (latest.is_within_range) fridgesOK++;
      else { fridgesOOR++; oorFridges.push({ fridge: f, log: latest }); }
    }

    // Cooling in progress
    const activeCooling = cooling.filter((r) => r.status === "in_progress" || r.status === "incomplete");

    // Expiring
    const expiringCount = expiringProducts.length;
    const expired = stats?.products?.expired || 0;

    // Checklist
    const checklistTotal = closingItems.length;
    const checklistPct = closingCompletedToday ? 100 : (checklistTotal > 0 ? 0 : 100);

    // Alerts count = fridges OOR + expiring soon + expired + active cooling
    const alertsCount = fridgesOOR + expiringCount + expired + activeCooling.length;

    // Global status ─ 3 states
    let globalState;
    if (fridgesOOR > 0 || expired > 0) globalState = "critical";
    else if (expiringCount > 0 || activeCooling.length > 0 || (checklistTotal > 0 && !closingCompletedToday) || fridgesWithoutTodayLog.length > 0) globalState = "attention";
    else globalState = "normal";

    return {
      fridgesTotal: refrigerators.length,
      fridgesOK, fridgesOOR,
      fridgesWithoutTodayLog,
      oorFridges,
      activeCooling,
      expiringCount, expired,
      checklistTotal, checklistPct,
      closingCompletedToday,
      alertsCount,
      globalState,
    };
  }, [data]);

  // ── Todos for today (task list) ──────────
  const todos = useMemo(() => {
    const list = [];
    if (facts.fridgesWithoutTodayLog.length > 0) {
      list.push({
        id: "temps",
        icon: Thermometer,
        label: t("dashboard.todo_temps").replace("{count}", facts.fridgesWithoutTodayLog.length),
        href: "/temperature",
      });
    }
    if (facts.expired > 0) {
      list.push({
        id: "expired",
        icon: AlertTriangle,
        label: t("dashboard.todo_expired").replace("{count}", facts.expired),
        href: "/products?status=expired",
        danger: true,
      });
    }
    if (facts.expiringCount > 0) {
      list.push({
        id: "expiring",
        icon: Package,
        label: t("dashboard.todo_expiring").replace("{count}", facts.expiringCount),
        href: "/products?status=warning",
      });
    }
    if (facts.checklistTotal > 0 && !facts.closingCompletedToday) {
      list.push({
        id: "checklist",
        icon: ClipboardList,
        label: t("dashboard.todo_checklist"),
        href: "/closing",
      });
    }
    if (facts.activeCooling.length > 0) {
      list.push({
        id: "cooling",
        icon: Wind,
        label: t("dashboard.todo_cooling").replace("{count}", facts.activeCooling.length),
        href: "/cooling-cell",
      });
    }
    return list;
  }, [facts, t]);

  // ── Active alerts (right column) ─────────
  const alerts = useMemo(() => {
    const list = [];
    // Fridges OOR
    for (const it of facts.oorFridges.slice(0, 5)) {
      list.push({
        kind: "temp-oor",
        icon: Thermometer,
        title: it.fridge.name,
        detail: `${it.log.temperature}°C`,
        time: it.log.logged_at,
        href: "/temperature",
        severity: "danger",
      });
    }
    // Expired products first, then expiring
    for (const p of data.expiringProducts.slice(0, 4)) {
      list.push({
        kind: "product",
        icon: Package,
        title: p.name,
        detail: p.status === "expired" ? "Caducado" : `Caduca ${p.expiration_date}`,
        time: p.expiration_date,
        href: `/products/${p.product_id}/edit`,
        severity: p.status === "expired" ? "danger" : "amber",
      });
    }
    // Checklist
    if (facts.checklistTotal > 0 && !facts.closingCompletedToday) {
      list.push({
        kind: "checklist",
        icon: ClipboardList,
        title: "Checklist",
        detail: `${facts.checklistTotal} pendientes`,
        time: null,
        href: "/closing",
        severity: "amber",
      });
    }
    // Cooling in progress
    for (const c of facts.activeCooling.slice(0, 3)) {
      list.push({
        kind: "cooling",
        icon: Wind,
        title: c.product_name,
        detail: "Enfriamiento en curso",
        time: c.created_at || c.date,
        href: "/cooling-cell",
        severity: "amber",
      });
    }
    return list;
  }, [facts, data.expiringProducts]);

  // ── Activity timeline ───────────────────
  const activity = useMemo(() => {
    const items = [];
    for (const l of data.tempLogs.slice(0, 20)) {
      const f = data.refrigerators.find((r) => r.refrigerator_id === l.refrigerator_id);
      items.push({
        ts: l.logged_at,
        icon: Thermometer,
        actor: l.created_by_name || null,
        verb: "registró temperatura",
        subject: f?.name || "—",
        value: `${l.temperature}°C`,
        oor: !l.is_within_range,
      });
    }
    for (const lp of data.labelHistory.slice(0, 15)) {
      items.push({
        ts: lp.printed_at || lp.created_at,
        icon: Tag,
        actor: lp.created_by_name || null,
        verb: "imprimió etiqueta DLC",
        subject: lp.product_name,
        value: null,
      });
    }
    for (const c of data.cooling.slice(0, 15)) {
      items.push({
        ts: c.created_at || c.date,
        icon: Wind,
        actor: c.created_by_name || null,
        verb: c.status === "complete" ? "finalizó enfriamiento" : "inició enfriamiento",
        subject: c.product_name,
        value: null,
      });
    }
    return items
      .filter((i) => i.ts)
      .sort((a, b) => new Date(b.ts) - new Date(a.ts))
      .slice(0, 8);
  }, [data.tempLogs, data.labelHistory, data.cooling, data.refrigerators]);

  // ── Render ───────────────────────────────
  if (loading) return <DashboardSkeleton />;
  if (loadError) return (
    <Card><CardContent className="p-6 flex flex-col items-center gap-2 text-destructive">
      <AlertTriangle className="h-6 w-6" /><p className="text-sm">Error al cargar el panel</p>
    </CardContent></Card>
  );

  const totalProducts = data.stats?.products?.total || 0;

  return (
    <div data-testid="dashboard-page" className="space-y-5 sm:space-y-6 animate-fade-in max-w-6xl mx-auto">

      <section className="dashboard-welcome flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2" aria-labelledby="dashboard-welcome-title">
        <div>
          <h1 id="dashboard-welcome-title" className="font-heading font-semibold text-2xl sm:text-3xl tracking-tight text-foreground">
            {lang === "fr" ? `Bonjour, ${firstName}` : `Buenos días, ${firstName}`}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {lang === "fr" ? "Voici l’état opérationnel du restaurant." : "Este es el estado operativo del restaurante."}
          </p>
        </div>
        <p className="text-xs text-muted-foreground capitalize">{todayLabel}</p>
      </section>

      {/* ══════════ BLOQUE 1 — Estado del restaurante ══════════ */}
      <StateBlock
        state={facts.globalState}
        loadedAt={loadedAt}
        dateLocale={dateLocale}
        t={t}
      />

      {/* ══════════ BLOQUE 2 — Resumen operativo (4 KPIs iguales) ══════════ */}
      <section aria-labelledby="summary-title">
        <h2 id="summary-title" className="sr-only">{t("dashboard.summary_title")}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Kpi
            icon={Package}
            label={t("dashboard.kpi_products_label")}
            value={totalProducts}
            href="/products"
            testid="kpi-products"
          />
          <Kpi
            icon={Thermometer}
            label={t("dashboard.kpi_temps_label")}
            value={facts.fridgesTotal > 0
              ? t("dashboard.kpi_temps_ok")
                  .replace("{ok}", facts.fridgesOK)
                  .replace("{total}", facts.fridgesTotal)
              : "—"}
            valueBig={false}
            tone={facts.fridgesOOR > 0 ? "danger" : "primary"}
            href="/temperature"
            testid="kpi-temps"
          />
          <Kpi
            icon={ClipboardList}
            label={t("dashboard.kpi_checklist_label")}
            value={`${facts.checklistPct}%`}
            tone={facts.closingCompletedToday ? "primary" : "amber"}
            href="/closing"
            testid="kpi-checklist"
          />
          <Kpi
            icon={AlertTriangle}
            label={t("dashboard.kpi_alerts_label")}
            value={facts.alertsCount}
            tone={facts.alertsCount === 0 ? "primary" : facts.fridgesOOR > 0 || facts.expired > 0 ? "danger" : "amber"}
            testid="kpi-alerts"
          />
        </div>
      </section>

      {/* ══════════ BLOQUE 3 — Cosas para hoy ══════════ */}
      <TodoBlock todos={todos} t={t} />

      {/* ══════════ BLOQUE 4 — Dos columnas ══════════ */}
      <section className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        <ActivityColumn items={activity} dateLocale={dateLocale} t={t} />
        <AlertsColumn items={alerts} dateLocale={dateLocale} t={t} />
      </section>

      {/* ══════════ BLOQUE 5 — Productos por caducar ══════════ */}
      <ExpiringBlock products={data.expiringProducts} t={t} />
    </div>
  );
}

// ─────────────────────────────────────────────
// BLOQUE 1
// ─────────────────────────────────────────────
function StateBlock({ state, loadedAt, dateLocale, t }) {
  const cfg = {
    normal:    { label: t("dashboard.state_normal"),    dotClass: "bg-emerald-500", txtClass: "text-primary", bandClass: "bg-primary", surfaceClass: "bg-emerald-50/45 border-emerald-100" },
    attention: { label: t("dashboard.state_attention"), dotClass: "bg-amber-500",   txtClass: "text-amber-700", bandClass: "bg-amber-500", surfaceClass: "bg-amber-50/55 border-amber-100" },
    critical:  { label: t("dashboard.state_critical"),  dotClass: "bg-red-500",     txtClass: "text-destructive", bandClass: "bg-destructive", surfaceClass: "bg-red-50/55 border-red-100" },
  }[state];

  let updatedAgo = null;
  if (loadedAt) {
    try { updatedAgo = formatDistanceToNow(loadedAt, { addSuffix: true, locale: dateLocale }); }
    catch { updatedAgo = null; }
  }

  return (
    <div
      className={`dashboard-state-card relative overflow-hidden rounded-2xl border p-6 sm:p-7 ${cfg.surfaceClass}`}
      data-testid="dashboard-state-block"
      data-state={state}
    >
      <div className={`absolute inset-y-0 left-0 w-1.5 ${cfg.bandClass}`} aria-hidden="true" />
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-medium">
        {t("dashboard.state_label")}
      </p>
      <div className="mt-2 flex items-baseline gap-3">
        <span
          className={`inline-block w-3 h-3 rounded-full ${cfg.dotClass} animate-pulse flex-shrink-0`}
          aria-hidden="true"
        />
        <h2
          className={`font-heading font-semibold text-3xl sm:text-4xl tracking-tight ${cfg.txtClass}`}
          data-testid="dashboard-state-label"
        >
          {cfg.label}
        </h2>
      </div>
      {updatedAgo && (
        <p className="text-xs text-muted-foreground mt-3">
          {t("dashboard.last_update")}: <span className="font-medium">{updatedAgo}</span>
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// KPI Card — reused, unified size
// ─────────────────────────────────────────────
function Kpi({ icon: Icon, label, value, valueBig = true, tone = "primary", href, testid }) {
  const tones = {
    primary: { bg: "bg-primary/10",  txt: "text-primary" },
    amber:   { bg: "bg-amber-50",    txt: "text-amber-700" },
    danger:  { bg: "bg-red-50",      txt: "text-destructive" },
  }[tone] || { bg: "bg-primary/10", txt: "text-primary" };

  const body = (
    <Card
      className="rounded-xl border-border/80 hover:-translate-y-0.5 hover:shadow-md hover:border-primary/25 transition-all duration-200 cursor-pointer h-full"
      data-testid={testid}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] sm:text-[11px] text-muted-foreground uppercase tracking-widest font-medium truncate">
            {label}
          </p>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tones.bg}`}>
            <Icon className={`h-3.5 w-3.5 ${tones.txt}`} />
          </div>
        </div>
        <p className={`font-bold font-heading tracking-tight ${tones.txt} truncate ${valueBig ? "text-2xl sm:text-3xl" : "text-base sm:text-lg"}`}>
          {value}
        </p>
      </CardContent>
    </Card>
  );

  return href ? <Link to={href} className="block h-full">{body}</Link> : body;
}

// ─────────────────────────────────────────────
// BLOQUE 3 — Todos for today
// ─────────────────────────────────────────────
function TodoBlock({ todos, t }) {
  if (todos.length === 0) {
    return (
      <Card
        className="border-emerald-200 bg-emerald-50/50"
        data-testid="todo-empty"
      >
        <CardContent className="p-5 sm:p-6 flex items-center gap-4">
          <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="h-5 w-5 text-emerald-700" />
          </div>
          <div>
            <p className="font-heading font-semibold text-base text-emerald-800">
              {t("dashboard.todo_all_done")}
            </p>
            <p className="text-xs text-emerald-700/80 mt-0.5">
              {t("dashboard.todo_all_done_hint")}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="border-primary/20 shadow-sm"
      data-testid="todo-block"
    >
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardList className="h-4 w-4 text-primary" />
          <h2 className="font-heading font-semibold text-base sm:text-lg text-foreground/90">
            {t("dashboard.todo_title")}
          </h2>
          <span className="ml-auto text-[11px] uppercase tracking-widest text-muted-foreground font-medium">
            {todos.length}
          </span>
        </div>
        <ul className="space-y-1">
          {todos.map((todo) => {
            const Icon = todo.icon;
            return (
              <li key={todo.id}>
                <Link
                  to={todo.href}
                  className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-md hover:bg-zinc-50/70 transition-colors group"
                  data-testid={`todo-${todo.id}`}
                >
                  <Circle className={`h-4 w-4 flex-shrink-0 ${todo.danger ? "text-destructive" : "text-primary/70"}`} strokeWidth={1.5} />
                  <Icon className={`h-4 w-4 flex-shrink-0 ${todo.danger ? "text-destructive" : "text-muted-foreground"}`} />
                  <span className={`flex-1 text-sm font-medium ${todo.danger ? "text-destructive" : "text-foreground"} truncate`}>
                    {todo.label}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </Link>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────
// BLOQUE 4 LEFT — Activity timeline
// ─────────────────────────────────────────────
function ActivityColumn({ items, dateLocale, t }) {
  return (
    <Card data-testid="activity-column">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <ActivityIcon className="h-4 w-4 text-primary" />
          <h2 className="font-heading font-semibold text-base text-foreground/90">
            {t("dashboard.activity_title")}
          </h2>
        </div>
        {items.length === 0 ? (
          <EmptyState icon={Clock} title={t("dashboard.activity_empty")} hint={t("dashboard.activity_empty_hint")} />
        ) : (
          <ul className="space-y-3 relative pl-4 border-l border-border/70">
            {items.map((it, idx) => {
              let hh = "—";
              try { hh = format(parseISO(it.ts), "HH:mm"); } catch { /* keep */ }
              const Icon = it.icon;
              return (
                <li key={`act-${idx}-${it.ts}`} className="relative">
                  <span
                    className={`absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-white ring-2 ${it.oor ? "bg-red-100 ring-destructive/40" : "bg-primary/20 ring-primary/30"}`}
                    aria-hidden="true"
                  />
                  <div className="flex items-start gap-2.5">
                    <span className="text-[11px] font-mono text-muted-foreground w-10 flex-shrink-0 pt-0.5">
                      {hh}
                    </span>
                    <Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${it.oor ? "text-destructive" : "text-primary"}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate leading-snug">
                        {it.actor ? (
                          <>
                            <span className="font-semibold text-foreground">{it.actor.split(" ")[0]}</span>{" "}
                            <span className="text-muted-foreground">{it.verb}</span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">{it.verb.charAt(0).toUpperCase() + it.verb.slice(1)}</span>
                        )}
                        {it.subject ? (
                          <span className="text-foreground/70"> · {it.subject}</span>
                        ) : null}
                      </p>
                    </div>
                    {it.value && (
                      <span className={`font-mono text-xs font-semibold flex-shrink-0 ${it.oor ? "text-destructive" : "text-foreground"}`}>
                        {it.value}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────
// BLOQUE 4 RIGHT — Active alerts
// ─────────────────────────────────────────────
function AlertsColumn({ items, dateLocale, t }) {
  if (items.length === 0) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/40" data-testid="alerts-column-empty">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-4 w-4 text-emerald-700" />
            <h2 className="font-heading font-semibold text-base text-emerald-800">
              {t("dashboard.alerts_title")}
            </h2>
          </div>
          <div className="flex items-center gap-3 py-2">
            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="h-4 w-4 text-emerald-700" />
            </div>
            <div>
              <p className="text-sm font-medium text-emerald-800">{t("dashboard.alerts_empty")}</p>
              <p className="text-[11px] text-emerald-700/80">{t("dashboard.alerts_empty_hint")}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-red-200/60" data-testid="alerts-column">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <h2 className="font-heading font-semibold text-base text-foreground/90">
            {t("dashboard.alerts_title")}
          </h2>
          <span className="ml-auto text-[11px] uppercase tracking-widest text-destructive font-semibold">
            {items.length}
          </span>
        </div>
        <ul className="space-y-2">
          {items.slice(0, 6).map((it, idx) => {
            const toneCfg = it.severity === "danger"
              ? { border: "border-red-200", bg: "bg-red-50/50 hover:bg-red-50", dot: "bg-red-500", txt: "text-destructive" }
              : { border: "border-amber-200", bg: "bg-amber-50/50 hover:bg-amber-50", dot: "bg-amber-500", txt: "text-amber-700" };
            let rel = null;
            if (it.time) {
              try { rel = formatDistanceToNow(parseISO(it.time), { addSuffix: true, locale: dateLocale }); }
              catch { rel = null; }
            }
            const Icon = it.icon;
            const inner = (
              <div className={`flex items-center gap-3 p-3 rounded-md border ${toneCfg.border} ${toneCfg.bg} transition-colors`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${toneCfg.dot} flex-shrink-0`} aria-hidden="true" />
                <Icon className={`h-4 w-4 flex-shrink-0 ${toneCfg.txt}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate text-foreground">{it.title}</p>
                  <p className={`text-[11px] truncate ${toneCfg.txt}`}>
                    {it.detail}{rel ? ` · ${rel}` : ""}
                  </p>
                </div>
                <ArrowRight className="h-3.5 w-3.5 opacity-40 flex-shrink-0" />
              </div>
            );
            return it.href ? (
              <li key={`alert-${idx}-${it.kind}`}>
                <Link to={it.href} className="block">{inner}</Link>
              </li>
            ) : <li key={`alert-${idx}-${it.kind}`}>{inner}</li>;
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────
// BLOQUE 5 — Expiring products table
// ─────────────────────────────────────────────
function ExpiringBlock({ products, t }) {
  return (
    <Card data-testid="expiring-block">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-semibold text-base text-foreground/90">
            {t("dashboard.expiring_title")}
          </h2>
          <Link to="/products?status=warning">
            <Button variant="ghost" size="sm" className="text-xs h-7" data-testid="view-all-expiring-btn">
              Ver todos <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </Link>
        </div>
        {products.length === 0 ? (
          <EmptyState icon={Sparkles} title={t("dashboard.expiring_empty")} hint={t("dashboard.expiring_empty_hint")} />
        ) : (
          <div className="overflow-hidden rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/70 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Producto</th>
                  <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">DLC</th>
                  <th className="text-left px-3 py-2 font-medium hidden md:table-cell">Categoría</th>
                  <th className="text-right px-3 py-2 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody>
                {products.slice(0, 6).map((p) => (
                  <tr key={p.product_id} className="border-t hover:bg-zinc-50/60 transition-colors">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 rounded bg-zinc-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {p.image_url ? (
                            <img src={p.image_url} alt={p.name} className="w-8 h-8 object-cover" />
                          ) : (
                            <Package className="h-3.5 w-3.5 text-zinc-400" />
                          )}
                        </div>
                        <span className="truncate font-medium">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground hidden sm:table-cell">
                      {p.expiration_date}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground hidden md:table-cell truncate">
                      {p.category}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <StatusBadge status={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────
// Shared empty state
// ─────────────────────────────────────────────
function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-1.5 py-8 text-muted-foreground">
      <Icon className="h-7 w-7 opacity-40" />
      <p className="text-sm font-medium">{title}</p>
      {hint && <p className="text-[11px]">{hint}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────
// Skeleton loading state
// ─────────────────────────────────────────────
function DashboardSkeleton() {
  return (
    <div className="space-y-6 sm:space-y-8 animate-pulse max-w-6xl mx-auto" data-testid="dashboard-skeleton">
      <div className="rounded-2xl border bg-white h-32" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={`kpi-sk-${i}`} className="rounded-lg border bg-white h-24" />
        ))}
      </div>
      <div className="rounded-lg border bg-white h-40" />
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="rounded-lg border bg-white h-64" />
        <div className="rounded-lg border bg-white h-64" />
      </div>
      <div className="rounded-lg border bg-white h-40" />
    </div>
  );
}
